import {
  BrowserCacheLocation,
  IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser";
import { environment } from "../../../environments/environment";

// Token sent as Bearer on every HttpClient request. microsoft-adal-angular6 did the same:
// URLs outside its `endpoints` map fell back to the app's clientId, so all APIs (escoutd/t/p)
// received the Roots token. Their App Service Authentication trusts the Roots registration
// and accepts this (v1-format) access token, but rejects MSAL's v2 ID token.
export const API_SCOPES = [`${environment.config.clientId}/.default`];

// Only our own servers get the token (ADAL also sent it to third parties such as file.io).
const TRUSTED_HOSTS = [
  "escoutd.azurewebsites.net", "escoutt.azurewebsites.net", "escoutp.azurewebsites.net",
  "escoutpreports.azurewebsites.net", "disscout.azurewebsites.net",
  "efproductionscoutlogs.azurewebsites.net", "efproductionscoutlogsdevelopment.azurewebsites.net",
  "esendgrid.azurewebsites.net",
  "eadmin.azurewebsites.net", "eadmin-dev.azurewebsites.net", "eadmin-staging.azurewebsites.net",
  "is-admin.azurewebsites.net", "localhost",
];

export function shouldAttachToken(url: string): boolean {
  if (!/^https?:\/\//i.test(url)) {
    return true; // relative URL -> same origin
  }
  const host = new URL(url).hostname.toLowerCase();
  return TRUSTED_HOSTS.includes(host) || host === "earthscout.com" || host.endsWith(".earthscout.com");
}

export const LOGIN_SCOPES = ["openid", "profile"];

export const msalInstance: IPublicClientApplication = new PublicClientApplication({
  auth: {
    clientId: environment.config.clientId,
    authority: `https://login.microsoftonline.com/${environment.config.tenant}`,
    redirectUri: environment.config.redirectUri,
    postLogoutRedirectUri: environment.config.postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
});

// Runs before the app bootstraps so that `isAuthenticated` is already correct
// when guards and components first read it (ADAL behaved the same way).
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();
  const result = await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
  const account = result?.account ?? msalInstance.getAllAccounts()[0];
  if (account) {
    msalInstance.setActiveAccount(account);
  }
}
